import BankIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Button,
  Card,
  CardActionArea,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Theme,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';

import Amex from '../Amex.svg';
import Diners from '../Diners.svg';
import Discover from '../Discover.svg';
import EmptyWalletLogo from '../EmptyWalletLogo.svg';
import {
  CardBrand,
  Customer,
  CustomerInfo,
  CustomerPaymentMethod,
  CustomerRole,
  PayerPaymentMethod,
  PaymentMethodRequest,
  PaymentMethodRequestStatus,
  PaymentMethodStatus,
  PaymentMethodType,
  Token,
} from '../gql-types.generated';
import Jcb from '../Jcb.svg';
import Mastercard from '../Mastercard.svg';
import Visa from '../Visa.svg';
import AlertBanner from './AlertBanner';
import PaymentMethodCard from './PaymentMethodCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridItem: {
      padding: theme.spacing(0.5, 2),
    },
    fontWeight: {
      fontWeight: 400,
    },
    cardActionContainer: {
      padding: theme.spacing(2),
    },
    addText: {
      textAlign: 'center',
      fontSize: '15px',
      fontWeight: 'bold',
      color: '#0275d8',
    },
    cardIcon: {
      fontSize: '48px',
      color: '#0275d8',
    },
    addIconContainer: {
      textAlign: 'center',
    },
    creditCardContainer: {
      padding: theme.spacing(0, 1, 2, 2),
    },
    bankCardContainer: {
      padding: theme.spacing(0, 2, 2, 1),
    },
    paymentMethodCard: {
      width: '100%',
      height: '100%',
      border: 'solid 1.5px #0275d8',
      borderRadius: theme.spacing(2),
      backgroundColor: '#FCFCFC',
    },
    emptyWalletLogo: {
      height: '4rem',
    },
  }),
);
interface PaymentMethodsProps {
  deleteMethod: (paymentMethod: PayerPaymentMethod) => void;
  paymentMethods?: PayerPaymentMethod[] | null;
  setAsDefault: (paymentMethod: PayerPaymentMethod) => void;
  deletePaymentMethodError?: Error;
  handleVerifyClick: (paymentMethod: PayerPaymentMethod) => void;
  handleAddCard: () => void;
  handleAddBank: () => void;
  forSelect?: boolean;
  handleSelectPaymentMethod?: (paymentMethod: PayerPaymentMethod) => void;
  selectedPaymentMethod?: PayerPaymentMethod;
  close?: () => void;
  paymentMethodError?: Error;
  customerRole: CustomerRole | undefined;
  customerInfo?: CustomerInfo;
  customerMethods: CustomerPaymentMethod[] | undefined | null;
  setSettingsMenuVisible: (isMenuVisible: boolean) => void;
  paymentMethodRequest?: PaymentMethodRequest;
  payerCustomer?: Customer;
}

export const getPaymentMethodText = (paymentMethod: PayerPaymentMethod): string => {
  if (paymentMethod.paymentMethod?.type === PaymentMethodType.CreditCard) {
    return `Card ending in ${paymentMethod.paymentMethod?.creditCard?.lastFour}`;
  }
  return `Account ending in ${paymentMethod.paymentMethod?.paymentBank?.lastFour}`;
};

export const getPaymentMethodIcon = (
  paymentMethod: PayerPaymentMethod | undefined,
  isDisabled: boolean,
  length: number,
  isCard?: boolean,
  methodToken?: Token,
): JSX.Element => {
  if (paymentMethod?.paymentMethod?.type === PaymentMethodType.PaymentBankUs) {
    return (
      <BankIcon
        style={{
          height: length,
          width: isCard ? length : length / 1.3,
          opacity: isDisabled ? 0.5 : 1,
          padding: isCard ? (length - length / 1.3) / 2 : 0,
        }}
      />
    );
  }
  const cardBrand = paymentMethod?.paymentMethod?.creditCard?.cardBrand || methodToken?.creditCard?.cardBrand;
  if (cardBrand === CardBrand.Amex) {
    return <img src={Amex} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }} alt="Americant Express"></img>;
  }
  if (cardBrand === CardBrand.Diners) {
    return <img src={Diners} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }} alt="Diners Club"></img>;
  }
  if (cardBrand === CardBrand.Discover) {
    return <img src={Discover} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }} alt="Discover"></img>;
  }
  if (cardBrand === CardBrand.Jcb) {
    return <img src={Jcb} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }} alt="JCB"></img>;
  }
  if (cardBrand === CardBrand.Mastercard) {
    return <img src={Mastercard} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }} alt="Mastercard"></img>;
  }
  if (cardBrand === CardBrand.Visa) {
    return <img src={Visa} style={{ height: length, width: length, opacity: isDisabled ? 0.5 : 1 }} alt="Visa"></img>;
  }
  return <HelpOutlineIcon />;
};

const PaymentMethods: React.FC<PaymentMethodsProps> = props => {
  const classes = useStyles();
  const {
    deleteMethod,
    paymentMethods,
    setAsDefault,
    deletePaymentMethodError,
    handleVerifyClick,
    handleAddCard,
    handleAddBank,
    handleSelectPaymentMethod,
    forSelect,
    selectedPaymentMethod,
    close,
    customerRole,
    customerInfo,
    customerMethods,
    setSettingsMenuVisible,
    paymentMethodRequest,
    payerCustomer,
  } = props;
  const [selectedMethod, setSelectedMethod] = useState<PayerPaymentMethod | null>(null);

  const isRestrictedRole =
    customerRole && (customerRole === CustomerRole.CustomerReader || customerRole === CustomerRole.CustomerPayer);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const MAX_CARDS = 5;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const MAX_BANKS = 1;
  let cardAmount = 0;
  let bankAmount = 0;
  paymentMethods?.forEach(method => {
    if (method.paymentMethod?.type === PaymentMethodType.CreditCard) {
      cardAmount += 1;
    }
    if (method.paymentMethod?.type === PaymentMethodType.PaymentBankUs) {
      bankAmount += 1;
    }
  });

  useEffect(() => {
    if (payerCustomer) {
      const hasSharedMethods =
        (customerMethods || []).filter(method => {
          return method.shareWithMerchant;
        }).length > 0;

      if (!hasSharedMethods) {
        setSettingsMenuVisible(true);
      }
    }

    if (paymentMethodRequest && paymentMethodRequest?.status === PaymentMethodRequestStatus.Pending) {
      setSettingsMenuVisible(true);
    }
  }, [payerCustomer, paymentMethodRequest]);

  const handleCardClicked = (paymentMethod: PayerPaymentMethod) => {
    if (paymentMethod === selectedMethod) {
      setSelectedMethod(null);
    } else {
      setSelectedMethod(paymentMethod);
    }
  };

  const handleSetDefaultClick = () => {
    if (selectedMethod) {
      setAsDefault(selectedMethod);
    }
  };

  const handleDeleteClick = () => {
    if (selectedMethod) {
      deleteMethod(selectedMethod);
    }
  };

  const bankAccount = paymentMethods?.find(method => {
    return method.paymentMethod?.type === PaymentMethodType.PaymentBankUs;
  });

  const handleChange = (event: { target: { value: string } }) => {
    if (forSelect) {
      const paymentMethodId = event.target.value;
      const paymentMethod = paymentMethods?.find(method => {
        return method?.paymentMethod?.id === paymentMethodId;
      });
      if (paymentMethod && handleSelectPaymentMethod) {
        handleSelectPaymentMethod(paymentMethod);
      }
      if (close) {
        close();
      }
    }
  };

  const findShareWithMerchant = (paymentMethodId: string | undefined): boolean => {
    if (!paymentMethodId) {
      return false;
    }
    const method = customerMethods?.find(method => {
      return method.paymentMethod?.id === paymentMethodId;
    });
    return !!method && (method.shareWithMerchant as boolean);
  };

  const isPayerOrCustomerSharedCard = (paymentMethodId: string | undefined): boolean => {
    if (!customerMethods) {
      return true;
    }
    return findShareWithMerchant(paymentMethodId);
  };

  if (forSelect && handleSelectPaymentMethod) {
    return (
      <Grid container>
        <FormControl>
          <RadioGroup value={selectedPaymentMethod} onChange={handleChange}>
            {paymentMethods &&
              paymentMethods.map(method => {
                return (
                  <Grid container item xs={12} key={method.paymentMethod?.id} className={classes.gridItem}>
                    <FormControlLabel
                      key={method.paymentMethod?.id}
                      value={method.paymentMethod?.id}
                      control={<Radio color="primary" />}
                      label={
                        <Grid item container alignItems="center" className={classes.fontWeight}>
                          {getPaymentMethodIcon(method, false, 48)}
                          <Typography>{getPaymentMethodText(method)}</Typography>
                        </Grid>
                      }
                    />
                  </Grid>
                );
              })}
          </RadioGroup>
        </FormControl>
        {cardAmount < MAX_CARDS && !isRestrictedRole && (
          <Grid item container xs={12} className={classes.gridItem}>
            <Button
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                handleAddCard();
              }}
              data-cy="add-credit-card"
            >
              ADD CREDIT CARD
            </Button>
          </Grid>
        )}
        {bankAmount < MAX_BANKS && !isRestrictedRole && (
          <Grid item container xs={12} className={classes.gridItem}>
            <Button variant="text" color="primary" startIcon={<AddIcon />} onClick={handleAddBank} data-cy="add-bank-account">
              ADD BANK ACCOUNT
            </Button>
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Grid container>
      {/* Show this error when there has been an error while deleting a payment method */}
      {deletePaymentMethodError && <AlertBanner message={deletePaymentMethodError.message} />}

      {/* Show this error when the bank account needs to be verified */}
      {bankAccount && bankAccount.paymentMethod?.status === PaymentMethodStatus.Unverified && (
        <AlertBanner message="Please verify your bank account" />
      )}

      {/* Show this error when the bank verification has failed and needs to be redone */}
      {bankAccount && bankAccount.paymentMethod?.status === PaymentMethodStatus.VerificationFailed && (
        <AlertBanner message="Verification failed. Please verify bank account again." />
      )}

      <Grid item container xs={12}>
        {(!paymentMethods || paymentMethods.length === 0) && (
          <>
            <Grid item container xs={12} justifyContent="center" style={{ paddingTop: 24 }}>
              <img src={EmptyWalletLogo} className={classes.emptyWalletLogo} />
            </Grid>
            <Grid item container xs={12} justifyContent="center" style={{ padding: '24px 8px' }}>
              <Typography variant="title">Wallet</Typography>
              <Typography align="center">There are no payment methods in this wallet.</Typography>
            </Grid>
          </>
        )}
        {paymentMethods &&
          paymentMethods.map(method => {
            return (
              <PaymentMethodCard
                key={paymentMethods?.indexOf(method)}
                method={method}
                isSelectedMethod={selectedMethod === method}
                isRestrictedRole={isRestrictedRole}
                customerInfo={customerInfo}
                handleCardClicked={handleCardClicked}
                handleSetDefaultClick={handleSetDefaultClick}
                handleDeleteClick={handleDeleteClick}
                handleVerifyClick={handleVerifyClick}
                findShareWithMerchant={findShareWithMerchant}
                isPayerOrCustomerSharedCard={isPayerOrCustomerSharedCard}
              />
            );
          })}
      </Grid>
      {cardAmount < MAX_CARDS && !isRestrictedRole && (
        <Grid item container xs={6} className={classes.creditCardContainer}>
          <Grid
            item
            xs={12}
            onClick={() => {
              handleAddCard();
            }}
            data-cy="add-credit-card"
          >
            <Card variant="outlined" className={classes.paymentMethodCard}>
              <CardActionArea className={classes.cardActionContainer}>
                <Grid item xs={12} className={classes.addIconContainer}>
                  <CreditCardIcon className={classes.cardIcon} />
                </Grid>
                <Grid item xs={12} className={classes.addText}>
                  Add Card
                </Grid>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      )}
      {bankAmount < MAX_BANKS && !isRestrictedRole && (
        <Grid item container xs={6} className={classes.bankCardContainer}>
          <Grid item xs={12} onClick={handleAddBank} data-cy="add-bank-account">
            <Card variant="outlined" className={classes.paymentMethodCard}>
              <CardActionArea className={classes.cardActionContainer}>
                <Grid item xs={12} className={classes.addIconContainer}>
                  <BankIcon className={classes.cardIcon} />
                </Grid>
                <Grid item xs={12} className={classes.addText}>
                  Add Account
                </Grid>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default PaymentMethods;
